import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';
import { AdminPageLayout, Card, StatCard, StatGrid, Section } from '../../components';
import clubDocumentApi from '../../services/clubDocumentApi';
import documentRequirementsService from '../../services/documentRequirementsService';
import apiService from '../../services/api';
import { FileText, ClipboardList, ArrowRight } from 'lucide-react';

const DocumentManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentStats, setDocumentStats] = useState(null);

  const isPlatformAdmin = user?.primary_role === 'admin';

  useEffect(() => {
    loadClubData();
  }, []);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const userInfo = await apiService.getCurrentUser();
      const club_id = userInfo.club_id;

      if (club_id) {
        const clubResponse = await apiService.get('/api/clubs/public/club-info');
        setClubData({
          id: club_id,
          name: clubResponse.name || clubResponse.display_name || 'Club Name',
          club_id: club_id
        });
      } else {
        setError('Club information not found');
      }
    } catch (err) {
      console.error('Error loading club data:', err);
      setError('Failed to load club information');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentStats = async () => {
    try {
      if (!clubData?.id) return;

      // Get club templates count
      const templatesResponse = await clubDocumentApi.getTemplates(clubData.id);
      const templates = templatesResponse.data?.templates || templatesResponse.templates || [];

      // Get compliance stats
      let compliancePercentage = 0;
      let totalRequirements = 0;
      try {
        const complianceStats = await documentRequirementsService.getComplianceStats();
        compliancePercentage = complianceStats.compliance_percentage || 0;
        totalRequirements = complianceStats.total_requirements || 0;
      } catch (err) {
        console.error('Error loading compliance stats:', err);
      }

      setDocumentStats({
        totalTemplates: templates.length,
        activeTemplates: templates.filter(t => t.active).length,
        totalRequirements: totalRequirements,
        compliancePercentage: compliancePercentage
      });
    } catch (err) {
      console.error('Error loading document stats:', err);
    }
  };

  useEffect(() => {
    loadClubData();
  }, []);

  useEffect(() => {
    if (clubData?.id) {
      loadDocumentStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubData]);

  if (loading) {
    return (
      <AdminPageLayout
        title="DOCUMENT MANAGEMENT"
        subtitle="Manage documents and requirements"
        maxWidth="1400px"
      >
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>
          Loading document management...
        </div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout
        title="DOCUMENT MANAGEMENT"
        subtitle="Manage documents and requirements"
        maxWidth="1400px"
      >
        <Section title="ERROR">
          <Card level={1} variant="error">
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--color-error)' }}>
                Error Loading Documents
              </div>
              <p style={{ marginBottom: '2rem', opacity: 0.8 }}>{error}</p>
              <Button onClick={loadClubData} variant="save">
                Retry
              </Button>
            </div>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="DOCUMENT MANAGEMENT"
      subtitle="Manage document templates and requirements for club members"
      maxWidth="1400px"
      primaryActions={
        <>
          <Button onClick={() => navigate('/admin/documents/requirements/create')} variant="outline">
            Create Requirement
          </Button>
          <Button onClick={() => navigate('/admin/club/document-templates/create')}>
            Create Template
          </Button>
        </>
      }
    >
      {/* Document Statistics */}
      {documentStats && (
        <StatGrid>
          <StatCard
            number={documentStats.totalTemplates}
            label="Document Templates"
            subtitle={`${documentStats.activeTemplates} active`}
          />
          <StatCard
            number={documentStats.totalRequirements}
            label="Document Requirements"
            subtitle="Configured rules"
          />
          <StatCard
            number={documentStats.compliancePercentage}
            label="Member Compliance"
            suffix="%"
            subtitle="Completed mandatory docs"
          />
        </StatGrid>
      )}

      {/* Management Options */}
      <Section title="MANAGEMENT HUBS">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '2rem',
          marginTop: '1rem'
        }}>
          <Card
            level={1}
            style={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              padding: '2rem'
            }}
            onClick={() => navigate('/admin/club/document-templates')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              <FileText size={48} />
            </div>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-text)' }}>
              DOCUMENT TEMPLATES
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Create and manage document templates that your club members will need to complete.
              Use the form designer to build custom waivers, medical forms, and registration documents.
            </p>
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)'
            }}>
              <span style={{ padding: '0.25rem 0.75rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
                {documentStats?.totalTemplates || 0} templates
              </span>
              <span style={{ padding: '0.25rem 0.75rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
                {documentStats?.activeTemplates || 0} active
              </span>
            </div>
            <Button variant="outline" style={{ width: '100%' }}>
              MANAGE TEMPLATES <ArrowRight size={16} />
            </Button>
          </Card>

          <Card
            level={1}
            style={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              padding: '2rem'
            }}
            onClick={() => navigate('/admin/documents/requirements')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              <ClipboardList size={48} />
            </div>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-text)' }}>
              DOCUMENT REQUIREMENTS
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Configure which document templates should be assigned to users based on their roles,
              programs, events, or other conditions. Set up automatic assignment rules.
            </p>
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)'
            }}>
              <span style={{ padding: '0.25rem 0.75rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
                {documentStats?.totalRequirements || 0} requirements
              </span>
              <span style={{ padding: '0.25rem 0.75rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
                {documentStats?.compliancePercentage || 0}% compliance
              </span>
            </div>
            <Button variant="outline" style={{ width: '100%' }}>
              MANAGE REQUIREMENTS <ArrowRight size={16} />
            </Button>
          </Card>
        </div>
      </Section>
    </AdminPageLayout>
  );
};

export default DocumentManagement;